'use client';

import { getClassNames } from '@sprd/sprd-component-kit/v2';
import { ReactNode, useState } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { AmplienceLinkType } from '@/types/amplience';
import { AppearanceConfigProvider } from '@/components/ProductCard/AppearanceConfigProvider';
import ProductCard, { ProductCardProps } from '@/components/ProductCard/ProductCard';
import ProgressBar from '@/components/ProgressBar/ProgressBar';
import Slider, { SliderProps } from '@/components/Slider/Slider';
import { SliderHeader } from '@/components/Slider/SliderHeader';
import { AppearanceImage } from '@/utility/appearanceConfig';
import { getModuleBackgroundStyles } from '@/utility/getModuleBackgroundStyles';

// Cards beyond this index render their appearance swatches client-side once they
// scroll near the viewport, keeping the SSR HTML small for off-screen cards.
const SSR_APPEARANCE_CARDS = 5;

export type ProductSliderProps = {
  className?: SliderProps['className'];
  heading?: string;
  items: ProductCardProps[];
  /** Deduplicated appearance lookup shared by all cards (see AppearanceConfigProvider). */
  appearanceConfig?: Record<string, AppearanceImage>;
  showAllLink?: AmplienceLinkType | AmplienceLinkType[];
  variant?: 'white' | 'gray' | 'black';
  children?: ReactNode;
  noPadding?: SliderProps['noPadding'];
  optimizeImages?: boolean;
  deferAppearances?: boolean;
  deferBadges?: boolean;
};

export default function ProductSlider(props: ProductSliderProps) {
  const {
    heading,
    items,
    appearanceConfig,
    showAllLink,
    className,
    children,
    noPadding,
    optimizeImages,
    deferAppearances,
    deferBadges,
    variant,
  } = props;
  const t = useTranslations('general');
  const [progress, setProgress] = useState(0);
  const headerLinks = Array.isArray(showAllLink)
    ? showAllLink.map((link) => ({ ...link, label: link.label || t('showAll') }))
    : showAllLink;

  const classNames = getClassNames(className);
  const { bgClass, textColor, isBackgroundBlack } = getModuleBackgroundStyles(variant);

  const slider = (
    <Slider
      className={{
        ...classNames,
        slider: clsx(classNames.slider),
        root: clsx(
          classNames.root,
          '[--slides-to-show:2.15] sm:[--slides-to-show:3.15] md:[--slides-to-show:4.18] lg:[--slides-to-show:4.2] xl:[--slides-to-show:4.15]'
        ),
        navPrev: clsx(classNames.navPrev, '!hidden bg-black text-white md:!flex lg:size-16'),
        navNext: clsx(classNames.navNext, '!hidden bg-black text-white md:!flex lg:size-16'),
      }}
      showNavigation
      onProgressChange={setProgress}
      noPadding={noPadding}
    >
      {items.map((item, index) => (
        <ProductCard
          key={index}
          imageBaseUrl={item.imageBaseUrl}
          images={item.images}
          imageSizes="(min-width: 768px) 33vw, (min-width: 1024px) 25vw, 50vw"
          link={item.link}
          price={item.price}
          currentDiscountAmount={item.currentDiscountAmount}
          priceFormat={item.priceFormat}
          title={item.title}
          subTitle={item.subTitle}
          brand={item.brand}
          labels={item.labels}
          appearanceIds={item.appearanceIds}
          appearances={item.appearances}
          favorite={item.favorite}
          background={item.background}
          productTypeId={item.productTypeId}
          dataTracking={item.dataTracking}
          optimizeImages={optimizeImages}
          deferAppearances={deferAppearances ?? index >= SSR_APPEARANCE_CARDS}
          deferBadges={deferBadges}
        />
      ))}
    </Slider>
  );

  return (
    <section className={clsx('gap-2', variant && [bgClass, textColor], classNames.root)}>
      <SliderHeader heading={heading} showAllLink={headerLinks} className={isBackgroundBlack ? 'text-white' : undefined}/>

      {/*
        Only install a provider when this slider owns the appearance config
        (homepage product-slider modules). In recommendation sliders the config
        lives in an ancestor AppearanceConfigProvider, so wrapping with an empty
        one here would shadow it and drop every card's swatches.
      */}
      {appearanceConfig ? (
        <AppearanceConfigProvider appearances={appearanceConfig}>{slider}</AppearanceConfigProvider>
      ) : (
        slider
      )}

      <div className="px-6 md:px-8 lg:px-16 xl:px-22.5">
        <ProgressBar progress={progress} isVisible={items.length > 4} />
      </div>

      {children}
    </section>
  );
}
